import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import {AttrsSchema, Component, jsx} from 'panel';
import type {VNode} from 'snabbdom/vnode';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {InsightsBookmarkParams, InsightsLegendUrlData, InsightsQueryResults} from 'common/report/util/insights/types';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {showHighCardinality} from 'common/report/util/insights/utils';
import {PieChartEl} from 'common/charts/visualization/pie-chart';
import {ChartDataItem} from 'common/charts/visualization/pie-chart/types';
import {SpinnerEl} from 'common/components/spinner';
import {SegmentLegendHeaderEl} from 'common/charts/misc/segment-legend-header';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';
import * as mpColors from 'common/util/colors';
import {computeMetricClauseByKey, renderPieChartTooltip} from 'common/report/util/insights/tooltip';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {useStore} from 'common/util/component-helpers';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getWorkspaceId} from 'common/report/util';
import {
  CSV_EXPORT_LIMIT,
  DEFAULT_SELECTED_SEGMENTS_LIMIT,
  MAX_SELECTED_SEGMENTS_LIMIT,
  OLD_DEFAULT_SELECTED_SEGMENTS_LIMIT,
  OLD_MAX_SELECTED_SEGMENTS_LIMIT,
  ReportName,
} from 'common/report/constants';

import css from './index.styl?inline';
import {getOtherSegment, getPieChartData, PieChartData} from './util';

interface InsightsPieChartState {
  pieChartData: PieChartData | null;
}

interface InsightsPieChartJsxProps {
  attrs: {
    loaded: boolean;
    failed: boolean;
  };
  props: {
    bookmarkParams: InsightsBookmarkParams;
    results: InsightsQueryResults;

    // legend data will eventually be persisted in bookmark, URL persistence is a stopgap
    urlLegendData?: InsightsLegendUrlData;

    // dataGroups will automatically be obtained from Stores without this prop,
    // but this prop is available for environments where Stores aren't supported (eg. Public Dashboards)
    dataGroups?: Record<string, DataGroup>;
  };
}
export class InsightsPieChart extends Component<
  InsightsPieChartState,
  InsightsPieChartJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InsightsPieChartJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      loaded: `boolean`,
      failed: `boolean`,
    };
  }

  private _bookmarkParams: InsightsBookmarkParams | null = null;
  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }
  public set bookmarkParams(value: InsightsBookmarkParams | null) {
    if (!isEqual(this._bookmarkParams, value)) {
      this._bookmarkParams = value;
      this.updatePieChartData();
    }
  }

  private _results: InsightsQueryResults | null = null;
  public get results(): InsightsQueryResults | null {
    return this._results;
  }
  public set results(value: InsightsQueryResults | null) {
    if (!isEqual(this._results, value)) {
      this._results = value;
      this.updatePieChartData();
    }
  }

  private _urlLegendData: InsightsLegendUrlData | null = null;
  public get urlLegendData(): InsightsLegendUrlData | null {
    return this._urlLegendData;
  }
  public set urlLegendData(value: InsightsLegendUrlData | null) {
    this._urlLegendData = value;
    this.updatePersistedLegendSelections();
  }

  private _dataGroups: Record<string, DataGroup> | null = null;
  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }
  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        pieChartData: null,
      },
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: showHighCardinality()
            ? DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Insights]
            : OLD_DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Insights],
          selectAllLimit: showHighCardinality()
            ? MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Insights]
            : OLD_MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Insights],
        }),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.dataGroups) {
      useStore(this, currentProjectStore, () => {
        if (currentProjectStore.value) {
          const projectId = currentProjectStore.value.id;
          const workspaceId = parseInt(getWorkspaceId(), 10);
          // Note: data groups are only used in tooltips and don't need to trigger state updates/renders or be annotated with people properties
          dataGroupStore.fetch({projectId, workspaceId}); // async, no need to wait
        }
      }).fetch();
    }

    this.updatePieChartData();
    this.updatePersistedLegendSelections();
  }

  private renderTemplate(): VNode {
    const loaded = this.attr(`loaded`) && this.hasRequiredParams() && !!this.state.pieChartData;
    if (!loaded) {
      return (
        <div sel='.root'>
          <SpinnerEl attrs={{elref: `loadingSpinner`}} />
        </div>
      );
    }

    if (isEmpty(this.results?.series)) {
      return (
        <div sel='.root'>
          <div sel='.empty-data' attrs={{elref: `emptyData`}}>
            <div sel='.empty-data-image-wrapper' />
            <div sel='.empty-data-text'>No data for this report</div>
          </div>
        </div>
      );
    }

    // TODO: failure state
    const visiblePieSeries = this.state.pieChartData.pieSeries.filter((chartSegment: ChartDataItem) => {
      return this.segmentFilterController.isItemSelected(0, chartSegment.name);
    });
    const showOtherSegment = this.segmentFilterController.isItemSelected(0, OTHER_SEGMENT_VALUE);
    const otherSegment = showOtherSegment
      ? getOtherSegment(this.state.pieChartData.segmentLookup, this.segmentFilterController)
      : null;
    if (otherSegment?.totalValue > 0) {
      visiblePieSeries.push({
        color: mpColors.otherSegment.primary,
        name: OTHER_SEGMENT_VALUE,
        displayName: `Other`,
        value: otherSegment.totalValue,
      });
    }

    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: this.getDataGroups(),
    });
    const tooltipFormatter = ({name, value, percent, color}) => {
      if (name === OTHER_SEGMENT_VALUE) {
        return renderPieChartTooltip({
          value,
          percent,
          color,
          otherMetricKeys: otherSegment.metricKeys,
          metricClauseByKey,
        });
      }

      const segment = this.state.pieChartData.segmentLookup.get(name);
      return renderPieChartTooltip({
        value,
        percent,
        color,
        metricKey: segment.path[0],
        segments: segment.path.slice(1),
        metricClauseByKey,
      });
    };
    return (
      <div sel='.root'>
        <SegmentLegendHeaderEl />
        <PieChartEl
          sel='.pie-chart'
          props={{
            chartData: visiblePieSeries,
            tooltipFormatter: metricClauseByKey ? tooltipFormatter : null,
          }}
          attrs={{
            elref: `pieChart`,
          }}
        />
      </div>
    );
  }

  private getDataGroups(): DataGroup[] {
    if (this.dataGroups) {
      return Object.values(this.dataGroups);
    } else if (dataGroupStore.value) {
      return Object.values(dataGroupStore.value);
    }
    return [];
  }

  private hasRequiredParams(): boolean {
    return !!(this.isConnected && this.bookmarkParams && this.results);
  }

  private updatePersistedLegendSelections(): void {
    const indexSelections = this.urlLegendData?.idxSelections;
    const isFlatSelection =
      Array.isArray(indexSelections) && indexSelections.length > 0 && !Array.isArray(indexSelections[0]);
    if (isFlatSelection && !!this.state.pieChartData) {
      const selectedIds = (indexSelections as number[]).map(
        (legendIndex) => this.state.pieChartData.legendIndexToId[legendIndex],
      );
      const isOtherSelected = this.urlLegendData.otherSelections?.[0];
      if (isOtherSelected) {
        selectedIds.push(OTHER_SEGMENT_VALUE);
      }
      this.segmentFilterController.loadPersistedSelections([selectedIds]);
    }
  }

  private updatePieChartData(): void {
    if (this.hasRequiredParams()) {
      const pieChartData = getPieChartData(this.results);
      this.update({pieChartData});

      const {meta} = this.results;

      let apiResultsTruncated = false;
      if (meta.report_sections.show) {
        apiResultsTruncated = some(meta.report_sections.show, `apiResultsTruncated`);
      }

      this.segmentFilterController.loadSections([
        {
          items: pieChartData.legendSegmentItems,
          metadata: pieChartData.legendIdToIndexMap, // Insights app is expected to use this mapping to persist legend selection indices to the URL
          apiResultsTruncated,
        },
      ]);

      this.segmentFilterController.segmentationQueryLimit = meta.queryLimits?.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = CSV_EXPORT_LIMIT[ReportName.Insights];
    } else {
      this.update({pieChartData: null});
    }
  }
}

export const InsightsPieChartEl = defineAndMakeJsxFactory<InsightsPieChartJsxProps>(
  `mp-insights-pie-chart`,
  InsightsPieChart,
);
